import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/authContext';
import AdminShell from '../components/admin/AdminShell';
import { adminVideosAPI } from '../utils/api/adminAPI';
import { Toast } from '../components/Auth/Toast';
import './ManageVideos.css';

function ManageVideos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    youtubeLink: '',
    thumbnail: '',
    category: 'viral'
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/');
    }
    fetchVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, page, search, category]);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const response = await adminVideosAPI.getAllVideos(page, 10, category, search);
      if (response.success) {
        setVideos(response.videos);
        setTotalPages(response.pages);
      }
    } catch (error) {
      setToast({
        type: 'error',
        message: '❌ Failed to fetch videos'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title || !formData.description || !formData.youtubeLink || !formData.category) {
      setToast({
        type: 'error',
        message: '❌ Please fill all required fields'
      });
      return;
    }

    try {
      setLoading(true);

      if (editingId) {
        // Update video
        const response = await adminVideosAPI.updateVideo(editingId, formData);
        if (response.success) {
          setToast({
            type: 'success',
            message: '✅ Video updated successfully'
          });
          setEditingId(null);
          setFormData({
            title: '',
            description: '',
            youtubeLink: '',
            thumbnail: '',
            category: 'viral'
          });
          setShowForm(false);
          fetchVideos();
        }
      } else {
        // Add new video
        const response = await adminVideosAPI.addVideo(formData);
        console.log('Add Video Response:', response); // Debug log
        if (response.success) {
          setToast({
            type: 'success',
            message: '✅ Video added successfully'
          });
          setFormData({
            title: '',
            description: '',
            youtubeLink: '',
            thumbnail: '',
            category: 'viral'
          });
          setShowForm(false);
          setPage(1);
          fetchVideos();
        } else {
          setToast({
            type: 'error',
            message: `❌ ${response.message || 'Failed to add video'}`
          });
        }
      }
    } catch (error) {
      console.error('Video submission error:', error); // Debug log
      setToast({
        type: 'error',
        message: `❌ ${error.message || 'Error adding video'}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (video) => {
    setFormData({
      title: video.title,
      description: video.description,
      youtubeLink: video.youtubeLink,
      thumbnail: video.thumbnail,
      category: video.category
    });
    setEditingId(video._id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const response = await adminVideosAPI.deleteVideo(deleteConfirm);
      if (response.success) {
        setToast({
          type: 'success',
          message: '✅ Video deleted successfully'
        });
        setDeleteConfirm(null);
        fetchVideos();
      }
    } catch (error) {
      setToast({
        type: 'error',
        message: '❌ Failed to delete video'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      title: '',
      description: '',
      youtubeLink: '',
      thumbnail: '',
      category: 'viral'
    });
  };

  const categories = ['All', 'viral', 'chaupal'];

  return (
    <AdminShell
      eyebrow="Video Desk"
      title="Manage videos"
      description="Review uploads, filter the library, and edit or publish video entries from one production-style workspace."
      primaryAction={{
        label: showForm ? 'Close editor' : 'Add new video',
        onClick: () => {
          if (showForm) {
            handleCancel();
            return;
          }

          setShowForm(true);
          setEditingId(null);
          setFormData({
            title: '',
            description: '',
            youtubeLink: '',
            thumbnail: '',
            category: 'viral'
          });
        }
      }}
      secondaryAction={{ label: 'Dashboard', onClick: () => navigate('/admin/dashboard') }}
      meta={[
        { label: 'Visible items', value: String(videos.length) },
        { label: 'Current page', value: `${page}/${Math.max(totalPages, 1)}` }
      ]}
    >
      <div className="manage-videos-page">
        <div className="manage-videos-header">
          <div>
            <h3>Video library controls</h3>
            <p>Search, filter, update metadata, and keep homepage video entries clean.</p>
          </div>
        </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="video-form-section">
          <h3>{editingId ? '✏️ Edit Video' : '➕ Add New Video'}</h3>
          <form onSubmit={handleSubmit} className="video-form">
            <div className="form-row">
              <div className="form-group full">
                <label>Video Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter video title"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full">
                <label>Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter video description"
                  rows="3"
                  required
                ></textarea>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>YouTube Link *</label>
                <input
                  type="url"
                  name="youtubeLink"
                  value={formData.youtubeLink}
                  onChange={handleInputChange}
                  placeholder="https://youtube.com/..."
                  required
                />
              </div>

              <div className="form-group">
                <label>Category *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="viral">🔥 Viral Videos</option>
                  <option value="chaupal">🎪 Chaupal Videos</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full">
                <label>Thumbnail URL</label>
                <input
                  type="url"
                  name="thumbnail"
                  value={formData.thumbnail}
                  onChange={handleInputChange}
                  placeholder="https://example.com/thumbnail.jpg"
                />
              </div>
            </div>

            <div className="form-buttons">
              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? '🔄 Processing...' : editingId ? '🔄 Update Video' : '➕ Add Video'}
              </button>
              <button type="button" className="cancel-btn" onClick={handleCancel}>
                ✕ Cancel
              </button>
            </div>
            <div className="form-note">
              <p>Note 1: YouTube link must be valid and publicly accessible. Thumbnail is optional; if not provided, it will be auto-generated from the YouTube link.</p>
              <p>Note 2: This latest video will be featured on the homepage.</p>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="videos-filters">
        <div className="filter-group">
          <input
            type="text"
            placeholder="🔍 Search videos..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <select
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setPage(1);
            }}
            className="category-select"
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Videos Table */}
      <div className="videos-table-section">
        {loading && !videos.length ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading videos...</p>
          </div>
        ) : videos.length === 0 ? (
          <div className="no-videos">
            <p>📭 No videos found</p>
            <button className="add-video-btn" onClick={() => setShowForm(true)}>
              ➕ Add First Video
            </button>
          </div>
        ) : (
          <>
            <div className="videos-table-wrapper">
              <table className="videos-table">
                <thead>
                  <tr>
                    <th>Video Title</th>
                    <th>Category</th>
                    <th>Uploaded</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {videos.map((video) => (
                    <tr key={video._id}>
                      <td>
                        <div className="video-cell-content">
                          <div className="video-title">{video.title}</div>
                          <div className="video-desc">{video.description?.substring(0, 50)}...</div>
                        </div>
                      </td>
                      <td>
                        <span className="category-badge">{video.category}</span>
                      </td>
                      <td>
                        {new Date(video.createdAt).toLocaleDateString()}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="edit-btn"
                            onClick={() => handleEdit(video)}
                            title="Edit video"
                          >
                            ✏️
                          </button>
                          <button
                            className="delete-btn"
                            onClick={() => setDeleteConfirm(video._id)}
                            title="Delete video"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                  className="page-btn"
                >
                  ◀ Previous
                </button>
                <span className="page-info">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={page === totalPages}
                  className="page-btn"
                >
                  Next ▶
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="delete-modal-overlay">
          <div className="delete-modal">
            <h3>⚠️ Confirm Delete</h3>
            <p>Are you sure you want to delete this video? This action cannot be undone.</p>
            <div className="modal-buttons">
              <button
                className="delete-confirm-btn"
                onClick={handleDelete}
                disabled={loading}
              >
                {loading ? '🔄 Deleting...' : '🗑️ Delete'}
              </button>
              <button
                className="modal-cancel-btn"
                onClick={() => setDeleteConfirm(null)}
              >
                ✕ Cancel
              </button>
            </div>
          </div>
        </div>
      )}

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </AdminShell>
  );
}

export default ManageVideos;
